import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAddressCard,
  faArrowUpRightFromSquare,
  faBriefcase,
  faBuilding,
  faCalendarDays,
  faEnvelope,
  faGlobe,
  faIdBadge,
  faLayerGroup,
  faListCheck,
  faPhone,
  faRotate,
  faShieldHalved,
  faSpinner,
  faTicket,
  faTriangleExclamation,
  faUser,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import useUserMenuItems from "../../components/Layout/useUserMenuItems";
import config from "../../config";
import { getById, getlist } from "../../contexts/apicontexts";
import { getTaskList } from "../Tasks/taskService";
import {
  getCurrentUserIdentity,
  getIdentityRoleLabel,
  getUserRoleNames,
  isCustomerIdentity,
  isEmployeeIdentity,
  normalizeTicketData,
} from "../Tickets/ticketUtils";

const normalizeEmployeeProfile = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  userId: String(item?.userID ?? item?.userId ?? item?.UserID ?? item?.UserId ?? ""),
  fullName: String(item?.fullName ?? item?.FullName ?? item?.name ?? item?.Name ?? ""),
  employeeCode: String(item?.employeeCode ?? item?.EmployeeCode ?? ""),
  employeeNumber: String(item?.employeeNumber ?? item?.EmployeeNumber ?? ""),
  workEmail: String(item?.workEmail ?? item?.WorkEmail ?? item?.email ?? item?.Email ?? ""),
  personalEmail: String(item?.personalEmail ?? item?.PersonalEmail ?? ""),
  mobile: String(item?.mobile ?? item?.Mobile ?? ""),
  roleName: String(item?.roleName ?? item?.RoleName ?? ""),
  departmentName: String(item?.departmentName ?? item?.DepartmentName ?? ""),
  designationName: String(item?.designationName ?? item?.DesignationName ?? ""),
  joiningDate: item?.joiningDate ?? item?.JoiningDate ?? "",
  employmentStatus: String(item?.employmentStatus ?? item?.EmploymentStatus ?? ""),
  photoUrlWithPath: String(item?.photoUrlWithPath ?? item?.PhotoUrlWithPath ?? ""),
});

const normalizeCustomerProfile = (item) => ({
  customerId: Number(item?.customerId ?? item?.CustomerId ?? item?.id ?? item?.Id ?? 0),
  userId: String(item?.userId ?? item?.UserId ?? item?.empUserId ?? item?.EmpUserId ?? ""),
  name: String(item?.name ?? item?.Name ?? ""),
  email: String(item?.email ?? item?.Email ?? ""),
  mobile: String(item?.mobile ?? item?.Mobile ?? ""),
  organizationName: String(item?.organizationName ?? item?.OrganizationName ?? ""),
  customerCode: String(item?.customerCode ?? item?.CustomerCode ?? ""),
  domain: String(item?.domain ?? item?.Domain ?? ""),
  address: String(item?.address ?? item?.Address ?? ""),
  agreementStartDate: item?.agreementStartDate ?? item?.AgreementStartDate ?? "",
  agreementEndDate: item?.agreementEndDate ?? item?.AgreementEndDate ?? "",
  isCurrentCustomer: String(item?.isCurrentCustomer ?? item?.IsCurrentCustomer ?? false).toLowerCase() === "true" || item?.isCurrentCustomer === true || item?.IsCurrentCustomer === true,
});

const flattenMenuLinks = (groups) => {
  const links = [];

  for (const group of groups || []) {
    for (const item of group?.items || []) {
      if (item?.to) links.push({ to: item.to, label: item.label, icon: item.icon });
      if (item?.isDropdown) {
        for (const sub of item?.subItems || []) {
          if (sub?.to) links.push({ to: sub.to, label: sub.label, icon: sub.icon });
        }
      }
    }
  }

  const seen = new Set();
  return links.filter((item) => {
    const key = String(item?.to || "").trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildInitials = (value) => {
  const words = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (!words.length) return "U";
  return words.map((item) => item[0]?.toUpperCase() || "").join("") || "U";
};

const getImageCandidates = (path) => {
  if (!path) return [];

  const normalizedPath = String(path).trim().replace(/\\/g, "/");
  if (!normalizedPath) return [];

  if (
    normalizedPath.startsWith("blob:") ||
    normalizedPath.startsWith("data:") ||
    normalizedPath.startsWith("http://") ||
    normalizedPath.startsWith("https://")
  ) {
    return [normalizedPath];
  }

  let cleanPath = normalizedPath.startsWith("~")
    ? normalizedPath.substring(1)
    : normalizedPath;

  if (!cleanPath.startsWith("/")) {
    cleanPath = `/${cleanPath}`;
  }

  cleanPath = cleanPath.replace(/\/{2,}/g, "/");

  let apiBase = "";
  let serverOrigin = "";

  try {
    const url = new URL(config.baseurl, window.location.origin);
    apiBase = url.href.replace(/\/+$/, "");
    serverOrigin = url.origin;
  } catch {
    apiBase = String(config.baseurl || window.location.origin).replace(/\/+$/, "");
    serverOrigin = window.location.origin;
  }

  const candidates = [
    `${apiBase}${cleanPath}`,
    `${serverOrigin}${cleanPath}`,
  ];

  return [...new Set(candidates.filter(Boolean))];
};

const formatDate = (value) => {
  if (!value) return "Not set";

  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const getTicketStats = (tickets) => {
  const list = Array.isArray(tickets) ? tickets : [];
  const unreadReplies = list.reduce((sum, item) => sum + Number(item?.unreadCount || 0), 0);
  const openCount = list.filter((item) => String(item?.status || "").toLowerCase() !== "resolved").length;

  return {
    total: list.length,
    unreadReplies,
    openCount,
  };
};

const getTaskStats = (tasks) => {
  const list = Array.isArray(tasks) ? tasks : [];
  const openCount = list.filter((item) => {
    const status = String(item?.status || "").toLowerCase();
    return status !== "done" && status !== "completed" && status !== "closed";
  }).length;

  return {
    total: list.length,
    openCount,
  };
};

const getActionDescription = (path) => {
  const value = String(path || "").toLowerCase();

  if (value.includes("/my-tasks") || value.includes("/all-tasks") || value.includes("/tasks")) return "Review and update your assigned work.";
  if (value.includes("/ticket")) return "Track support requests and replies.";
  if (value.includes("/customer-users")) return "Manage customer logins and access.";
  if (value.includes("/customer")) return "Open customer-related account records.";
  if (value.includes("/employee")) return "Check employee information and work details.";
  if (value.includes("/roles")) return "Review role and access setup.";
  if (value.includes("/dashboard")) return "Return to your main workspace overview.";
  return "Open this area from your current permissions.";
};

const findEmployeeRecord = (employees, identity) => {
  const userId = String(identity?.userId || "");
  const email = String(identity?.email || "").trim().toLowerCase();

  return (
    employees.find((item) => userId && String(item.userId || "") === userId) ||
    employees.find(
      (item) =>
        email &&
        [item.workEmail, item.personalEmail]
          .filter(Boolean)
          .some((value) => String(value).trim().toLowerCase() === email)
    ) ||
    null
  );
};

const StatCard = ({ label, value, helper, icon, tone = "slate" }) => {
  const tones = {
    slate: "bg-slate-900 text-white",
    amber: "bg-amber-400 text-slate-950",
    blue: "bg-sky-500 text-white",
    emerald: "bg-emerald-500 text-white",
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-500 dark:text-slate-300">{label}</div>
          <div className="mt-1 truncate text-3xl font-semibold text-slate-900 dark:text-white">{value}</div>
          {helper ? <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helper}</div> : null}
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tones[tone] || tones.slate}`}>
          <FontAwesomeIcon icon={icon} />
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-900/40">
    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700">
      <FontAwesomeIcon icon={icon} />
    </div>
    <div className="min-w-0">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">{label}</div>
      <div className="mt-1 break-words text-sm font-semibold text-slate-900 dark:text-white">{value || "Not available"}</div>
    </div>
  </div>
);

const SectionCard = ({ eyebrow, title, description, children }) => (
  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
    <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
      {eyebrow ? <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">{eyebrow}</div> : null}
      <h2 className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">{title}</h2>
      {description ? <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">{description}</p> : null}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Profile = () => {
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const { menuItems, loading: menuLoading } = useUserMenuItems();
  const roleNames = useMemo(() => getUserRoleNames(identity.raw), [identity.raw]);
  const roleLabel = useMemo(() => getIdentityRoleLabel(identity), [identity]);
  const isCustomerView = useMemo(() => isCustomerIdentity(identity), [identity]);
  const isEmployeeView = useMemo(() => isEmployeeIdentity(identity), [identity]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profileState, setProfileState] = useState({
    employee: null,
    customer: null,
    tickets: [],
    tasks: [],
    assignedCustomers: [],
  });

  const loadProfile = React.useCallback(async () => {
    setLoading(true);
    setError("");

    const requests = [];

    if (config.baseurl) {
      requests.push(getlist(`${config.baseurl}Employee/getlist`));
      requests.push(
        isCustomerView
          ? getById(`${config.baseurl}Customer/dashboard`)
          : Promise.resolve(null)
      );
      requests.push(
        isCustomerView
          ? getlist(`${config.baseurl}Ticket/getlist`)
          : identity.userId
            ? getlist(`${config.baseurl}Ticket/getassignedlist?userId=${encodeURIComponent(identity.userId)}`)
            : Promise.resolve(null)
      );
      requests.push(
        isEmployeeView ? getlist(`${config.baseurl}Customer/getmylist`) : Promise.resolve(null)
      );
    } else {
      requests.push(Promise.resolve(null), Promise.resolve(null), Promise.resolve(null), Promise.resolve(null));
    }

    requests.push(identity.userId && !isCustomerView ? getTaskList({ scopeUserId: identity.userId }) : Promise.resolve([]));

    const [employeeResult, customerResult, ticketResult, customerListResult, taskResult] = await Promise.allSettled(requests);

    const employees =
      employeeResult.status === "fulfilled" &&
      employeeResult.value?.status === 200 &&
      Array.isArray(employeeResult.value.data)
        ? employeeResult.value.data.map(normalizeEmployeeProfile)
        : [];

    const customer =
      customerResult.status === "fulfilled" &&
      customerResult.value?.status === 200 &&
      customerResult.value?.data
        ? normalizeCustomerProfile(customerResult.value.data)
        : null;

    const tickets =
      ticketResult.status === "fulfilled" &&
      ticketResult.value?.status === 200 &&
      Array.isArray(ticketResult.value.data)
        ? ticketResult.value.data.map(normalizeTicketData)
        : [];

    const assignedCustomers =
      customerListResult.status === "fulfilled" &&
      customerListResult.value?.status === 200 &&
      Array.isArray(customerListResult.value.data)
        ? customerListResult.value.data.map(normalizeCustomerProfile)
        : [];

    const tasks = taskResult.status === "fulfilled" && Array.isArray(taskResult.value) ? taskResult.value : [];

    setProfileState({
      employee: findEmployeeRecord(employees, identity),
      customer,
      tickets,
      tasks,
      assignedCustomers,
    });

    const failedCount = [employeeResult, customerResult, ticketResult, customerListResult, taskResult].filter(
      (item) => item.status === "rejected"
    ).length;

    if (failedCount >= 2) {
      setError("Some live profile data could not be loaded. Showing the best available login information.");
    }

    setLoading(false);
  }, [identity, isCustomerView, isEmployeeView]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const ticketStats = useMemo(() => getTicketStats(profileState.tickets), [profileState.tickets]);
  const taskStats = useMemo(() => getTaskStats(profileState.tasks), [profileState.tasks]);

  const profile = useMemo(() => {
    const localUser = identity.raw || {};
    const employee = profileState.employee;
    const customer = profileState.customer;
    const name =
      employee?.fullName ||
      customer?.name ||
      identity.fullName ||
      localUser?.userName ||
      localUser?.username ||
      identity.email ||
      "User";

    return {
      name,
      email: localUser?.workEmail || localUser?.WorkEmail || localUser?.email || localUser?.Email || employee?.workEmail || customer?.email || identity.email || "",
      personalEmail: localUser?.personalEmail || localUser?.PersonalEmail || employee?.personalEmail || "",
      phone: localUser?.mobile || localUser?.Mobile || localUser?.phone || localUser?.Phone || employee?.mobile || customer?.mobile || "",
      userId: identity.userId || employee?.userId || customer?.userId || "",
      roleName: localUser?.roleName || localUser?.RoleName || employee?.roleName || roleNames[0] || roleLabel,
      roleNames: roleNames.length ? roleNames : [roleLabel],
      designation: localUser?.designationName || localUser?.DesignationName || localUser?.designation || localUser?.Designation || employee?.designationName || "",
      department: localUser?.departmentName || localUser?.DepartmentName || localUser?.department || localUser?.Department || employee?.departmentName || "",
      employeeCode: localUser?.employeeCode || localUser?.EmployeeCode || localUser?.code || localUser?.Code || employee?.employeeCode || "",
      employeeNumber: localUser?.employeeNumber || localUser?.EmployeeNumber || employee?.employeeNumber || "",
      organizationName: localUser?.organizationName || localUser?.OrganizationName || customer?.organizationName || "",
      customerCode: localUser?.customerCode || localUser?.CustomerCode || customer?.customerCode || "",
      domain: localUser?.domain || localUser?.Domain || customer?.domain || "",
      address: localUser?.address || localUser?.Address || customer?.address || "",
      joinedDate: employee?.joiningDate || customer?.agreementStartDate || "",
      reviewDate: customer?.agreementEndDate || "",
      status: isCustomerView
        ? customer?.isCurrentCustomer === false
          ? "Inactive Customer"
          : "Active Customer"
        : employee?.employmentStatus || "Active",
      photoPath:
        localUser?.photoUrlWithPath ||
        localUser?.PhotoUrlWithPath ||
        employee?.photoUrlWithPath ||
        "",
      sourceLabel: employee
        ? "Synced from employee profile"
        : customer
          ? "Synced from customer account"
          : "Using login session information",
    };
  }, [identity, isCustomerView, profileState.customer, profileState.employee, roleLabel, roleNames]);

  const quickActions = useMemo(() => {
    const actions = flattenMenuLinks(menuItems).filter((item) => item.to !== "/profile");
    const priorities = isCustomerView
      ? ["/dashboard", "/tickets", "/client/customer-users", "/client/customer"]
      : ["/dashboard", "/my-tasks", "/all-tasks", "/assigned-tickets", "/client/my-customers", "/hr/employee", "/user/alluser", "/user/roles"];

    const ordered = priorities
      .map((path) => actions.find((item) => String(item.to || "").toLowerCase().startsWith(path)))
      .filter(Boolean);
    const rest = actions.filter(
      (item) => !ordered.some((picked) => String(picked.to || "").toLowerCase() === String(item.to || "").toLowerCase())
    );

    return [...ordered, ...rest].slice(0, 6);
  }, [isCustomerView, menuItems]);

  const photoCandidates = useMemo(() => getImageCandidates(profile.photoPath), [profile.photoPath]);

  const handleProfileImageError = (event) => {
    const currentIndex = Number(event.currentTarget.dataset.fallbackIndex || "0");
    const nextIndex = currentIndex + 1;

    if (nextIndex < photoCandidates.length) {
      event.currentTarget.dataset.fallbackIndex = String(nextIndex);
      event.currentTarget.src = photoCandidates[nextIndex];
      return;
    }

    event.currentTarget.style.display = "none";
    const fallback = event.currentTarget.parentElement?.querySelector("[data-profile-fallback='true']");
    if (fallback) {
      fallback.style.display = "flex";
    }
  };

  const statCards = useMemo(() => {
    const common = [
      {
        label: "Modules",
        value: menuItems.length,
        helper: menuLoading ? "Loading access..." : "Visible menu groups",
        icon: faLayerGroup,
        tone: "slate",
      },
      {
        label: "Tickets",
        value: ticketStats.total,
        helper: isCustomerView ? "Available from your account" : "Assigned to your login",
        icon: faTicket,
        tone: "amber",
      },
      {
        label: "Unread Replies",
        value: ticketStats.unreadReplies,
        helper: "New ticket conversation updates",
        icon: faEnvelope,
        tone: "blue",
      },
    ];

    if (isCustomerView) {
      return [
        ...common,
        {
          label: "Open Tickets",
          value: ticketStats.openCount,
          helper: "Still waiting on completion",
          icon: faShieldHalved,
          tone: "emerald",
        },
      ];
    }

    return [
      ...common,
      {
        label: isEmployeeView ? "My Customers" : "Open Tasks",
        value: isEmployeeView ? profileState.assignedCustomers.length : taskStats.openCount,
        helper: isEmployeeView ? "Accounts linked to your work" : "Still in progress",
        icon: isEmployeeView ? faUserGroup : faListCheck,
        tone: "emerald",
      },
    ];
  }, [
    isCustomerView,
    isEmployeeView,
    menuItems.length,
    menuLoading,
    profileState.assignedCustomers.length,
    taskStats.openCount,
    ticketStats.openCount,
    ticketStats.total,
    ticketStats.unreadReplies,
  ]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 dark:bg-slate-950 md:p-6">
      <Breadcrumb pageName="User Profile" currentPage="Profile" />

      <div className="mt-4 overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.22),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(245,158,11,0.18),_transparent_30%),linear-gradient(135deg,#0f172a,#172554_50%,#1e293b)] px-5 py-6 text-white md:px-7 md:py-8">
          <div className="absolute right-0 top-0 h-40 w-40 translate-x-10 -translate-y-10 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <div className="shrink-0">
                <div className="relative">
                  {photoCandidates.length ? (
                    <img
                      src={photoCandidates[0]}
                      alt={profile.name}
                      className="h-24 w-24 rounded-[28px] border border-white/15 object-cover shadow-lg"
                      data-fallback-index="0"
                      onError={handleProfileImageError}
                    />
                  ) : null}
                  <div
                    data-profile-fallback="true"
                    className="h-24 w-24 items-center justify-center rounded-[28px] border border-white/15 bg-white/10 text-3xl font-semibold text-white shadow-lg dark:bg-slate-800/70"
                    style={{ display: photoCandidates.length ? "none" : "flex" }}
                  >
                    {buildInitials(profile.name)}
                  </div>
                </div>
              </div>

              <div className="min-w-0">
                <div className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-100">
                  {profile.sourceLabel}
                </div>
                <h1 className="mt-3 truncate text-2xl font-semibold tracking-tight md:text-3xl">{profile.name}</h1>
                <p className="mt-2 text-sm text-slate-200 md:text-base">
                  {profile.designation || profile.roleName || roleLabel}
                  {profile.department ? ` | ${profile.department}` : ""}
                  {profile.organizationName ? ` | ${profile.organizationName}` : ""}
                </p>
                <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-200">
                  <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{roleLabel}</span>
                  <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{profile.status}</span>
                  <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">
                    User ID: {profile.userId || "Unavailable"}
                  </span>
                  {profile.email ? (
                    <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{profile.email}</span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={loadProfile}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                Refresh
              </button>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-amber-300"
              >
                Go to dashboard
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              </Link>
            </div>
          </div>
        </div>

        <div className="grid gap-4 border-b border-slate-200 bg-slate-50/80 p-5 dark:border-slate-700 dark:bg-slate-900/80 md:grid-cols-2 xl:grid-cols-4 md:p-6">
          {statCards.map((item) => (
            <StatCard
              key={item.label}
              label={item.label}
              value={item.value}
              helper={item.helper}
              icon={item.icon}
              tone={item.tone}
            />
          ))}
        </div>

        <div className="p-5 md:p-6">
          {error ? (
            <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" />
              {error}
            </div>
          ) : null}

          <div className="grid gap-6 xl:grid-cols-[1.15fr,0.85fr]">
            <div className="space-y-6">
              <SectionCard
                eyebrow="Contact"
                title="Profile Details"
                description="Primary information for the currently logged-in user."
              >
                <div className="grid gap-3 md:grid-cols-2">
                  <InfoRow icon={faEnvelope} label="Work Email" value={profile.email} />
                  <InfoRow icon={faPhone} label="Phone" value={profile.phone} />
                  <InfoRow icon={faAddressCard} label="Personal Email" value={profile.personalEmail} />
                  <InfoRow icon={faIdBadge} label="Role Names" value={profile.roleNames.join(", ")} />
                </div>
              </SectionCard>

              <SectionCard
                eyebrow={isCustomerView ? "Account" : "Work"}
                title={isCustomerView ? "Customer Account Snapshot" : "Work Profile"}
                description={
                  isCustomerView
                    ? "Business details connected with this login."
                    : "Employee and work-related details linked with your login."
                }
              >
                <div className="grid gap-3 md:grid-cols-2">
                  <InfoRow icon={faBriefcase} label={isCustomerView ? "Customer Code" : "Designation"} value={isCustomerView ? profile.customerCode : profile.designation} />
                  <InfoRow icon={faBuilding} label={isCustomerView ? "Organization" : "Department"} value={isCustomerView ? profile.organizationName : profile.department} />
                  <InfoRow icon={faIdBadge} label={isCustomerView ? "Agreement Start" : "Employee Code"} value={isCustomerView ? formatDate(profile.joinedDate) : profile.employeeCode} />
                  <InfoRow icon={faCalendarDays} label={isCustomerView ? "Agreement End" : "Joining Date"} value={isCustomerView ? formatDate(profile.reviewDate) : formatDate(profile.joinedDate)} />
                  {isCustomerView ? <InfoRow icon={faGlobe} label="Domain" value={profile.domain} /> : null}
                  {!isCustomerView ? <InfoRow icon={faAddressCard} label="Employee Number" value={profile.employeeNumber} /> : null}
                  {isCustomerView && profile.address ? <InfoRow icon={faBuilding} label="Address" value={profile.address} /> : null}
                </div>
              </SectionCard>
            </div>

            <div className="space-y-6">
              <SectionCard
                eyebrow="Related Work"
                title="Quick Actions"
                description="Open the areas most relevant to this login and role."
              >
                <div className="space-y-3">
                  {quickActions.length ? (
                    quickActions.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="group flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 transition hover:border-slate-300 hover:bg-white dark:border-slate-700 dark:bg-slate-900/40 dark:hover:border-slate-600 dark:hover:bg-slate-900"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white dark:bg-slate-800">
                            <FontAwesomeIcon icon={item.icon || faUser} />
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.label}</div>
                            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{getActionDescription(item.to)}</div>
                          </div>
                        </div>
                        <FontAwesomeIcon
                          icon={faArrowUpRightFromSquare}
                          className="text-slate-400 transition group-hover:text-slate-700 dark:text-slate-500 dark:group-hover:text-slate-200"
                        />
                      </Link>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
                      {menuLoading ? "Loading your accessible pages..." : "No quick actions found for this account."}
                    </div>
                  )}
                </div>
              </SectionCard>

              <SectionCard
                eyebrow="Summary"
                title="Current Workspace Focus"
                description="A short summary of what is currently related to this login."
              >
                <div className="space-y-3">
                  <InfoRow icon={faTicket} label="Ticket Summary" value={`${ticketStats.total} total | ${ticketStats.openCount} open | ${ticketStats.unreadReplies} unread replies`} />
                  {!isCustomerView ? (
                    <InfoRow
                      icon={faListCheck}
                      label="Task Summary"
                      value={`${taskStats.total} total | ${taskStats.openCount} still open`}
                    />
                  ) : null}
                  {isEmployeeView ? (
                    <InfoRow
                      icon={faUserGroup}
                      label="Assigned Customers"
                      value={`${profileState.assignedCustomers.length} linked customer accounts`}
                    />
                  ) : null}
                  <InfoRow icon={faLayerGroup} label="Accessible Modules" value={`${menuItems.length} visible groups in your menu`} />
                </div>
              </SectionCard>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;

